import { IconReceipt, IconBuildingBank, IconAddressBook, IconLock, IconBuildingStore, IconDatabase } from '@tabler/icons-react'
import PortalTabs, { PortalTab } from '@/components/portal/PortalTabs.tsx'
import { toast } from 'react-toastify'
import { useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { useClient } from '@/stores/useClient.ts'
import { usePortalContext } from '@/pages/portal/Portal.page.tsx'
import BillingAddressForm from '@/components/portal/BillingAddressForm.tsx'
import { UpdateProfAccountRequest } from '@/models/billingAddress.ts'
import { useState } from 'react'
import { Input } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { ProfAccount } from '@/models/profAccount.ts'
import PortalStandardComponent from '@/components/portal/PortalStandardComponent.tsx'
import { useProfile } from '@/stores/useAuth.ts'
import ContactForm, { ContactFormSubmit } from '@/components/portal/ContactForm.tsx'
import BrandsSettingsSection from '@/components/portal/BrandsSettingsSection.tsx'
import StorageSettingsSection from '@/components/portal/StorageSettingsSection.tsx'
import { getApiErrorCode, getApiErrorStatus, getApiFormErrors } from '@/controller/apiErrors.ts'

const settingsTabs: PortalTab[] = [
  { id: 'billing', label: 'Rechnungsadresse', icon: IconReceipt },
  { id: 'payout', label: 'Auszahlungsdaten', icon: IconBuildingBank },
  { id: 'contact', label: 'Kontakt', icon: IconAddressBook },
  { id: 'brands', label: 'Marken', icon: IconBuildingStore },
  { id: 'password', label: 'Passwort', icon: IconLock },
  { id: 'storage', label: 'Speicher', icon: IconDatabase },
]

export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const portalContext = usePortalContext()
  const { updatePortalMine } = useClient()
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)

  const activeTab = searchParams.get('tab') || 'billing'

  function setActiveTab(tab: string) {
    setSearchParams(tab === 'billing' ? {} : { tab })
  }

  if (!portalContext?.account) {
    return <div>Kein Account geladen</div>
  }

  const account = portalContext.account

  async function handleBillingSubmit(data: UpdateProfAccountRequest) {
    setIsLoading(true)
    try {
      await updatePortalMine(data)
      queryClient.invalidateQueries({ queryKey: ['portal', 'mine'] })
      toast('Rechnungsadresse gespeichert', { closeButton: true, autoClose: 3000 })
    } catch {
      toast('Fehler beim Speichern', { closeButton: true, autoClose: 3000, type: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <PortalStandardComponent title="Einstellungen" description="Verwalte deine Rechnungsadresse, Auszahlungsdaten, Kontaktinformationen, Marken, dein Passwort und deinen Speicher." noCard>
      <PortalTabs tabs={settingsTabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {activeTab === 'billing' && (
        <section className="bg-neutral p-5 max-w-xl">
          <BillingAddressForm
            initialData={account as any}
            onSubmit={handleBillingSubmit}
            isLoading={isLoading}
          />
        </section>
      )}

      {activeTab === 'payout' && (
        <PayoutSettingsSection account={account} />
      )}

      {activeTab === 'contact' && (
        <ContactSettingsSection account={account} />
      )}

      {activeTab === 'brands' && (
        <BrandsSettingsSection account={account} />
      )}

      {activeTab === 'password' && (
        <PasswordSettingsSection />
      )}

      {activeTab === 'storage' && (
        <StorageSettingsSection />
      )}
    </PortalStandardComponent>
  )
}

function PayoutSettingsSection({ account }: { account: ProfAccount }) {
  const { updatePortalMine } = useClient()
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)
  const [iban, setIban] = useState(account.iban || '')
  const [recipientName, setRecipientName] = useState(account.iban_recipient_name || '')
  const [isKleinunternehmer, setIsKleinunternehmer] = useState(account.is_kleinunternehmer)
  const [vatRate, setVatRate] = useState(account.vat_rate ?? '')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setIsLoading(true)
    try {
      await updatePortalMine({
        iban: iban.trim(),
        iban_recipient_name: recipientName.trim(),
        is_kleinunternehmer: isKleinunternehmer,
        vat_rate: isKleinunternehmer ? null : (vatRate !== '' ? parseFloat(String(vatRate)) : null),
      })
      queryClient.invalidateQueries({ queryKey: ['portal', 'mine'] })
      toast('Auszahlungsdaten gespeichert', { closeButton: true, autoClose: 3000 })
    } catch {
      toast('Fehler beim Speichern', { closeButton: true, autoClose: 3000, type: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <section className="bg-neutral p-5 max-w-xl">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <label className="form-control w-full">
          <div className="label">
            <span className="label-text">IBAN</span>
          </div>
          <Input
            value={iban}
            onChange={e => setIban(e.target.value)}
            placeholder="DE89 3704 0044 0532 0130 00"
          />
        </label>

        <label className="form-control w-full">
          <div className="label">
            <span className="label-text">Empfängername</span>
          </div>
          <Input
            value={recipientName}
            onChange={e => setRecipientName(e.target.value)}
            placeholder="Max Mustermann Fotografie"
          />
        </label>

        <div className="form-control">
          <label className="label cursor-pointer justify-start gap-3">
            <input
              type="checkbox"
              className="toggle toggle-primary"
              checked={isKleinunternehmer}
              onChange={e => setIsKleinunternehmer(e.target.checked)}
            />
            <span className="label-text">Kleinunternehmerregelung</span>
          </label>
          <div className="label pt-0">
            <span className="label-text-alt opacity-60">
              Kleinunternehmer sind von der Umsatzsteuer befreit (§ 19 UStG).
            </span>
          </div>
        </div>

        {!isKleinunternehmer && (
          <label className="form-control w-full max-w-xs">
            <div className="label">
              <span className="label-text">MwSt-Satz (%)</span>
            </div>
            <Input
              type="number"
              min="0"
              max="30"
              step="0.1"
              value={vatRate}
              onChange={e => setVatRate(e.target.value)}
              placeholder="19.0"
            />
          </label>
        )}

        <Button type="submit" color="primary" loading={isLoading} disabled={isLoading}>
          Speichern
        </Button>
      </form>
    </section>
  )
}

function ContactSettingsSection({ account }: { account: ProfAccount }) {
  const { updatePortalMine } = useClient()
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)

  async function handleSubmit(data: ContactFormSubmit) {
    setIsLoading(true)
    try {
      await updatePortalMine(data)
      queryClient.invalidateQueries({ queryKey: ['portal', 'mine'] })
      toast('Kontaktdaten gespeichert', { closeButton: true, autoClose: 3000 })
    } catch (e) {
      toast('Fehler beim Speichern', { closeButton: true, autoClose: 3000, type: 'error' })
      throw e
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <section className="bg-neutral p-5 max-w-xl">
      <ContactForm initialData={account} onSubmit={handleSubmit} isLoading={isLoading} />
    </section>
  )
}

/**
 * Set or change the password. Two states, decided solely by `profile.has_password`:
 * without one, the valid token is already the proof of ownership and no current
 * password is asked for; with one, `current_password` is required.
 *
 * Reached from the login link mail via `/portal/settings?tab=password`.
 * The token stays valid throughout — a password change never logs anyone out.
 */
function PasswordSettingsSection() {
  const { setPassword: setPasswordRequest } = useClient()
  const { profile, setProfile } = useProfile()
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [repeatPassword, setRepeatPassword] = useState('')
  const [errors, setErrors] = useState<string[]>([])

  // The backend can disagree with what we last knew (password set in another
  // tab), so `password_already_set` flips this on without a reload.
  const [needsCurrentPassword, setNeedsCurrentPassword] = useState(false)
  const hasPassword = profile?.has_password === true || needsCurrentPassword

  function resetFields() {
    setCurrentPassword('')
    setNewPassword('')
    setRepeatPassword('')
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (isLoading) {
      return
    }

    if (newPassword !== repeatPassword) {
      setErrors(['Die beiden Passwörter stimmen nicht überein.'])
      return
    }

    setIsLoading(true)
    setErrors([])

    try {
      const res = await setPasswordRequest({
        new_password: newPassword,
        ...(hasPassword ? { current_password: currentPassword } : {}),
      })
      // has_password is true from here on — keep the store and the portal query
      // in sync so the form switches to "ändern" straight away.
      setProfile(res.data.profile)
      queryClient.invalidateQueries({ queryKey: ['portal', 'mine'] })
      setNeedsCurrentPassword(false)
      resetFields()
      toast(hasPassword ? 'Passwort geändert' : 'Passwort festgelegt', { closeButton: true, autoClose: 3000 })
    } catch (e: any) {
      const status = getApiErrorStatus(e)
      const code = getApiErrorCode(e)

      if (status === 429) {
        setErrors(['Zu viele Versuche. Bitte probiere es später erneut.'])
      } else if (code === 'invalid_current_password') {
        setErrors(['Das aktuelle Passwort ist falsch.'])
        setCurrentPassword('')
      } else if (code === 'password_already_set') {
        setNeedsCurrentPassword(true)
        setErrors(['Für diesen Account ist bereits ein Passwort gesetzt. Bitte gib dein aktuelles Passwort ein.'])
      } else if (code === 'invalid_data') {
        // The backend's validation messages are written for the user — show them as they are.
        const messages = getApiFormErrors(e, 'new_password')
        setErrors(messages.length ? messages : ['Das Passwort erfüllt die Anforderungen nicht.'])
      } else {
        console.error('Set password error', e)
        setErrors(['Das Passwort konnte nicht gespeichert werden. Bitte versuche es erneut.'])
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <section className="bg-neutral p-5 max-w-xl">
      <h2 className="font-serif text-xl mb-1">{hasPassword ? 'Passwort ändern' : 'Passwort festlegen'}</h2>
      <p className="text-sm opacity-60 mb-4">
        Ein Passwort ist optional. Du kannst dich jederzeit weiterhin mit einem Login-Link per E-Mail anmelden.
      </p>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {hasPassword && (
          <label className="form-control w-full">
            <div className="label">
              <span className="label-text">Aktuelles Passwort</span>
            </div>
            <Input
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
            />
          </label>
        )}

        <label className="form-control w-full">
          <div className="label">
            <span className="label-text">Neues Passwort</span>
          </div>
          <Input
            type="password"
            autoComplete="new-password"
            required
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
          />
        </label>

        <label className="form-control w-full">
          <div className="label">
            <span className="label-text">Neues Passwort wiederholen</span>
          </div>
          <Input
            type="password"
            autoComplete="new-password"
            required
            value={repeatPassword}
            onChange={e => setRepeatPassword(e.target.value)}
          />
        </label>

        {errors.length > 0 && (
          <div className="flex flex-col gap-1">
            {errors.map((message, index) => (
              <p key={index} className="text-error text-sm">{message}</p>
            ))}
          </div>
        )}

        <Button type="submit" color="primary" loading={isLoading} disabled={isLoading}>
          {hasPassword ? 'Passwort ändern' : 'Passwort festlegen'}
        </Button>
      </form>
    </section>
  )
}
